/**
 * Kind-scoped art quality gates (no Phaser).
 * SSOT: docs/testing/Game_Proto_64px_캐릭터_품질게이트.md (multi-kind sections)
 *
 * Character anatomy stays in measureProportionQuality (continuity.ts).
 * Do NOT apply head/leg metrics to tiles/structures.
 */

import { solidCount } from "./continuity";

export type SpriteKind = "character" | "structure" | "tile" | "prop" | "vfx";

export type KindQualityReport = {
  kind: SpriteKind;
  solidCount: number;
  uniqueOpaqueKeys: number;
  bboxWidth: number;
  bboxHeight: number;
  ok: boolean;
  failReasons: string[];
};

export type TileQualityReport = KindQualityReport & {
  fillRatio: number;
  edgeOpaque: boolean;
};

export const DF_COMBAT_IDS = [
  "assault",
  "shooter",
  "engineer",
  "swarm",
] as const;

export const DF_STRUCTURE_IDS = [
  "hq",
  "depot",
  "nest",
  "turret",
  "beacon",
  "barrier",
  "pod",
] as const;

export const DF_TILE_IDS = [
  "tile_floor",
  "tile_floor_alt",
  "tile_rift",
  "tile_rift_edge",
] as const;

/** Default Dropfront spriteKinds — pack.json may override. */
export const DROPFRONT_SPRITE_KINDS: Record<string, SpriteKind> = {
  assault: "character",
  shooter: "character",
  engineer: "character",
  swarm: "character",
  hq: "structure",
  depot: "structure",
  nest: "structure",
  turret: "structure",
  beacon: "structure",
  barrier: "structure",
  pod: "structure",
  tile_floor: "tile",
  tile_floor_alt: "tile",
  tile_rift: "tile",
  tile_rift_edge: "tile",
};

export const MIN_COMBAT_HAMMING = 120;
export const MIN_STRUCTURE_HAMMING = 80;

function bbox(rows: string[]) {
  let minX = 64;
  let maxX = -1;
  let minY = 64;
  let maxY = -1;
  for (let y = 0; y < rows.length; y++) {
    const r = rows[y]!;
    for (let x = 0; x < r.length; x++) {
      if (r[x] === ".") continue;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0, w: 0, h: 0 };
  return {
    minX,
    maxX,
    minY,
    maxY,
    w: maxX - minX + 1,
    h: maxY - minY + 1,
  };
}

function uniqueKeys(rows: string[]): number {
  const s = new Set<string>();
  for (const row of rows) {
    for (const ch of row) {
      if (ch !== ".") s.add(ch);
    }
  }
  return s.size;
}

/** Opaque silhouette as flat boolean mask (row-major). */
export function silhouetteBits(rows: string[]): boolean[] {
  const out: boolean[] = [];
  for (const row of rows) {
    for (const ch of row) out.push(ch !== ".");
  }
  return out;
}

export function hammingBits(a: boolean[], b: boolean[]): number {
  const n = Math.min(a.length, b.length);
  let d = 0;
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) d += 1;
  d += Math.abs(a.length - b.length);
  return d;
}

/** Full palette-key distance (for fully-opaque tiles where silhouette ham is 0). */
export function paletteHamming(a: string[], b: string[]): number {
  let d = 0;
  const h = Math.min(a.length, b.length);
  for (let y = 0; y < h; y++) {
    const ra = a[y]!;
    const rb = b[y]!;
    const w = Math.min(ra.length, rb.length);
    for (let x = 0; x < w; x++) if (ra[x] !== rb[x]) d += 1;
    d += Math.abs(ra.length - rb.length);
  }
  d += Math.abs(a.length - b.length) * (a[0]?.length ?? 0);
  return d;
}

export function pairwiseMinHamming(frames: string[][]): number {
  if (frames.length < 2) return Infinity;
  let min = Infinity;
  const masks = frames.map(silhouetteBits);
  for (let i = 0; i < masks.length; i++) {
    for (let j = i + 1; j < masks.length; j++) {
      min = Math.min(min, hammingBits(masks[i]!, masks[j]!));
    }
  }
  return min;
}

export function measureStructureQuality(rows: string[]): KindQualityReport {
  const b = bbox(rows);
  const solid = solidCount(rows);
  const keys = uniqueKeys(rows);
  const failReasons: string[] = [];
  if (b.w < 16 || b.w > 56 || b.h < 20 || b.h > 58) {
    failReasons.push(`bbox ${b.w}x${b.h} not in 16–56 × 20–58`);
  }
  if (solid < 180 || solid > 1400) {
    failReasons.push(`solid ${solid} not in 180–1400`);
  }
  if (keys < 5) failReasons.push(`keys ${keys} < 5`);
  return {
    kind: "structure",
    solidCount: solid,
    uniqueOpaqueKeys: keys,
    bboxWidth: b.w,
    bboxHeight: b.h,
    ok: failReasons.length === 0,
    failReasons,
  };
}

export function measureTileQuality(rows: string[]): TileQualityReport {
  const b = bbox(rows);
  const solid = solidCount(rows);
  const keys = uniqueKeys(rows);
  const area = rows.length * (rows[0]?.length ?? 0);
  const fillRatio = area > 0 ? solid / area : 0;
  let edgeOpaque = false;
  const h = rows.length;
  const w = rows[0]?.length ?? 0;
  for (let x = 0; x < w; x++) {
    if (rows[0]?.[x] !== "." || rows[h - 1]?.[x] !== ".") edgeOpaque = true;
  }
  for (let y = 0; y < h; y++) {
    if (rows[y]?.[0] !== "." || rows[y]?.[w - 1] !== ".") edgeOpaque = true;
  }
  const failReasons: string[] = [];
  if (fillRatio < 0.35 || fillRatio > 1.0) {
    failReasons.push(`fillRatio ${fillRatio.toFixed(2)} not in 0.35–1.0`);
  }
  if (!edgeOpaque) failReasons.push("no edge opaque pixels");
  if (keys < 2 || keys > 5) failReasons.push(`keys ${keys} not in 2–5`);
  if (b.w < 40 || b.h < 40) {
    failReasons.push(`bbox ${b.w}x${b.h} too small for tile`);
  }
  return {
    kind: "tile",
    solidCount: solid,
    uniqueOpaqueKeys: keys,
    bboxWidth: b.w,
    bboxHeight: b.h,
    fillRatio,
    edgeOpaque,
    ok: failReasons.length === 0,
    failReasons,
  };
}

export function measurePropQuality(rows: string[]): KindQualityReport {
  const b = bbox(rows);
  const solid = solidCount(rows);
  const keys = uniqueKeys(rows);
  const failReasons: string[] = [];
  if (solid < 40 || solid > 900) {
    failReasons.push(`solid ${solid} not in 40–900`);
  }
  if (keys < 4) failReasons.push(`keys ${keys} < 4`);
  if (b.w < 8 || b.h < 8) failReasons.push(`bbox ${b.w}x${b.h} too small`);
  return {
    kind: "prop",
    solidCount: solid,
    uniqueOpaqueKeys: keys,
    bboxWidth: b.w,
    bboxHeight: b.h,
    ok: failReasons.length === 0,
    failReasons,
  };
}

export function resolveSpriteKind(
  spriteId: string,
  kinds?: Record<string, SpriteKind>,
): SpriteKind {
  return kinds?.[spriteId] ?? DROPFRONT_SPRITE_KINDS[spriteId] ?? "prop";
}
